// ============================================================================
// CreateQueuePingModal — confirm + send a Queue Ping from an active fire.
//
// Prefilled from the fire (purpose → label / action / target). Sender may add a
// short non-PHI note. Send calls the createQueuePing Cloud Function, which builds
// the trusted recipient list server-side. NON-PHI only.
// ============================================================================

import React, { useState } from 'react';
import { Megaphone, X, Loader2, Users, AlertTriangle, CheckCircle2 } from 'lucide-react';
import {
  createQueuePingCF,
  QUEUE_PING_PURPOSE_LABELS,
  QUEUE_PING_ACTION_TEXT,
  QUEUE_PING_PURPOSE_SECTOR,
  QUEUE_PING_SECTOR_ROLE_MAP,
  QUEUE_PING_NOTE_MAX_LENGTH,
} from '../core/index.js';
import { ROLE_DISPLAY } from '../core/rbac.js';

const SECTOR_LABEL = {
  INTAKE: 'Intake Coordinators',
  TRIAGE: 'Triage Nurses',
  SCHEDULING: 'Schedulers',
  CLOSED_LOOP: 'Specialist Admins',
};

export default function CreateQueuePingModal({ purpose, source = 'LIVE_FIRES', onClose, onSent }) {
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null); // { recipientCount }

  const label = QUEUE_PING_PURPOSE_LABELS[purpose] || purpose;
  const action = QUEUE_PING_ACTION_TEXT[purpose] || '';
  const sector = QUEUE_PING_PURPOSE_SECTOR[purpose];
  const targetRole = QUEUE_PING_SECTOR_ROLE_MAP[sector];
  const targetLabel = SECTOR_LABEL[sector] || sector;

  const send = async () => {
    if (sending) return;
    setSending(true);
    setError('');
    try {
      const res = await createQueuePingCF({ source, purpose, note: note.trim() || null });
      setDone({ recipientCount: res?.recipientCount ?? 0 });
      onSent?.(res);
    } catch (err) {
      const msg = err?.message || '';
      // CF throws failed-precondition when no active recipients exist.
      setError(/no active/i.test(msg)
        ? `No active ${ROLE_DISPLAY[targetRole]?.name ?? targetRole} users to notify. No queue ping was created.`
        : 'Could not send queue ping. Please retry.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700/60 rounded-2xl max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800/60">
          <span className="inline-flex items-center gap-2 text-amber-300 text-sm font-semibold">
            <Megaphone className="w-4 h-4" /> Send Queue Ping
          </span>
          <button onClick={onClose} className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        {done ? (
          <div className="p-6 text-center space-y-3">
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto" />
            <p className="text-sm text-slate-200">Queue ping sent to {done.recipientCount} user{done.recipientCount === 1 ? '' : 's'}.</p>
            <button onClick={onClose} className="px-4 py-2 rounded-xl bg-slate-700 hover:bg-slate-600 text-white text-sm">Close</button>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div>
              <p className="text-base font-semibold text-white">{label}</p>
              <p className="text-xs text-slate-400 mt-1 inline-flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" /> Target: {targetLabel}
              </p>
            </div>
            <div className="rounded-lg bg-slate-800/50 border border-slate-700/40 px-3 py-2.5">
              <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Action needed</p>
              <p className="text-sm text-slate-200 leading-relaxed">{action}</p>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                Note <span className="normal-case text-slate-600">(optional · non-PHI · max {QUEUE_PING_NOTE_MAX_LENGTH})</span>
              </label>
              <input
                type="text"
                maxLength={QUEUE_PING_NOTE_MAX_LENGTH}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Short non-PHI note…"
                disabled={sending}
                className="w-full px-2.5 py-1.5 rounded-lg bg-slate-800/60 border border-slate-700/50 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-amber-500/50"
              />
            </div>
            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-300">{error}</p>
              </div>
            )}
            <div className="flex gap-2 justify-end pt-1">
              <button onClick={onClose} disabled={sending} className="px-4 py-2 rounded-xl border border-slate-700/50 text-slate-300 hover:text-white text-sm disabled:opacity-50">Cancel</button>
              <button onClick={send} disabled={sending} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white text-sm font-medium">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />} Send Queue Ping
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
